import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigation, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { deleteVolunteerTeam } from '@/data/tasks';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { showAlert } from '@/utils/showAlert';
import usePlatform from '@/utils/hooks/usePlatform';

const TeamCard = ({ team, loggedInUser, onDelete }) => {
    const router = useRouter();
    const assignerCount = team.Assigners?.length || 0;
    const options = [];

    if (loggedInUser?.can?.('Update Tasks')) {
        options.push({ name: 'Edit Team', icon: dropdownIcons.edit, onSelect: () => router.push({ pathname: '/tasks/teams/create', params: { id: team.id } }) });
    }
    if (loggedInUser?.can?.('Delete Tasks')) {
        options.push({
            name: 'Delete Team',
            icon: dropdownIcons.delete,
            destructive: true,
            onSelect: () => showAlert({
                title: 'Delete Team',
                subtitle: `Are you sure you want to delete "${team.name}"?`,
                confirmText: 'Delete',
                isDestructive: true,
                onConfirm: () => onDelete(team.id),
            }),
        });
    }

    return (
        <View testID={`TeamCard-${team.name}`} style={styles.card}>
            <TouchableOpacity testID={`TeamCardLink-${team.id}`} accessibilityRole="button" accessibilityLabel={`Open ${team.name} team`} style={styles.cardLink} onPress={() => router.push(`/tasks/teams/${team.id}`)} activeOpacity={0.7}>
                <View style={styles.icon}><Unicon name="people-outline" size={22} colorVariant="tasks" /></View>
                <View style={styles.cardCopy}>
                    <Text style={styles.cardTitle}>{team.name}</Text>
                    {!!team.description && <Text style={styles.cardDescription} numberOfLines={2}>{team.description}</Text>}
                    <View style={styles.metaRow}>
                        <View style={styles.metaItem}><Unicon name="clipboard-outline" size={13} colorVariant="textSecondary" /><Text style={styles.metaText}>{team.Tasks?.length || 0} tasks</Text></View>
                        {!!team.Location?.name && <View style={styles.metaItem}><Unicon name="location-outline" size={13} colorVariant="textSecondary" /><Text style={styles.metaText}>{team.Location.name}</Text></View>}
                        <View style={styles.assignerStatus(assignerCount > 0)}><Unicon name={assignerCount > 0 ? 'person-outline' : 'alert-circle-outline'} size={13} colorVariant={assignerCount > 0 ? 'tasks' : 'warning'} /><Text style={styles.assignerText(assignerCount > 0)}>{assignerCount > 0 ? `${assignerCount} assigner${assignerCount === 1 ? '' : 's'}` : 'No assigner'}</Text></View>
                    </View>
                </View>
            </TouchableOpacity>
            {options.length > 0 && <DropdownMenu testID={`TeamCardMenu-${team.id}`} options={options} />}
        </View>
    );
};

export default function TaskTeamsScreen() {
    const [searchText, setSearchText] = useState('');
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { isNative } = usePlatform();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: teams = [], isLoading, isFetching } = Query.taskTeams.all();
    const deleteMutation = useMutation({
        mutationFn: deleteVolunteerTeam,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskTeams.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.all() });
        },
    });

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => loggedInUser?.can?.('Create Tasks') ? (
                <Unicon onPress={() => router.push('/tasks/teams/create')} name="add" size={24} colorVariant="text" />
            ) : null,
        });
    }, [loggedInUser, navigation, router, isNative]);

    const filteredTeams = useMemo(() => teams.filter(team => {
        const search = searchText.toLowerCase();
        return !search || team.name?.toLowerCase().includes(search) || team.description?.toLowerCase().includes(search) || team.Location?.name?.toLowerCase().includes(search);
    }), [searchText, teams]);

    const refresh = useCallback(() => queryClient.invalidateQueries({ queryKey: QueryKeys.taskTeams.all() }), [queryClient]);

    return (
        <ScrollView style={styles.root} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={!isLoading && isFetching} onRefresh={refresh} />}>
            <View style={styles.header}>
                <View style={styles.headingCopy}>
                    <Text style={styles.eyebrow}>TEAM OPERATIONS</Text>
                    <Text style={styles.heading}>All teams</Text>
                    <Text style={styles.subheading}>Open a team to review its work, events, and 30-day coverage.</Text>
                </View>
                {loggedInUser?.can?.('Create Tasks') && <SolidButton testID="TaskTeamsCreate" text="Create team" iconName="add" iconBefore onPress={() => router.push('/tasks/teams/create')} />}
            </View>
            <SearchBox testID="TaskTeamsSearch" searchText={searchText} onChangeText={setSearchText} textChangeDelay={0} placeholder="Search teams..." />
            <View style={styles.list}>
                {isLoading ? <ActivityIndicator style={styles.loading} /> : filteredTeams.length === 0 ? <EmptyListPlaceholder spacing={10} message={searchText ? 'No teams match your search.' : 'No task teams yet.'} /> : filteredTeams.map(team => <TeamCard key={team.id} team={team} loggedInUser={loggedInUser} onDelete={teamID => deleteMutation.mutate(teamID)} />)}
            </View>
        </ScrollView>
    );
}

const styles = StyleSheet.create(theme => ({
    root: { flex: 1, backgroundColor: 'transparent' },
    content: { width: '100%', maxWidth: 920, alignSelf: 'center', padding: 16, paddingBottom: 100, gap: 14 },
    header: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 },
    headingCopy: { flex: 1, minWidth: 250 },
    eyebrow: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 11, letterSpacing: 1.2 },
    heading: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 24, marginTop: 3 },
    subheading: { color: theme.colors.textSecondary, fontSize: 14, marginTop: 3 },
    list: { gap: 10 },
    loading: { marginVertical: 48 },
    card: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme.colors.surface, borderRadius: 14, padding: 14, boxShadow: theme.shadows.card },
    cardLink: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 13 },
    icon: { width: 46, height: 46, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.tasksContainer },
    cardCopy: { flex: 1 },
    cardTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 16 },
    cardDescription: { color: theme.colors.textSecondary, fontSize: 13, lineHeight: 18, marginTop: 2 },
    metaRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 7 },
    metaItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    metaText: { color: theme.colors.textSecondary, fontSize: 11 },
    assignerStatus: assigned => ({ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: assigned ? theme.colors.tasksContainer : theme.colors.warningContainer, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 7 }),
    assignerText: assigned => ({ color: assigned ? theme.colors.tasks : theme.colors.warning, fontFamily: theme.fonts.bold, fontSize: 11 }),
}));
